import {
    PRE_DEFINED_FIELD,
    TECHNOGRAPHICS_FIELD,
} from "app/pages/sales-intelligence/common-components/export-table/const";
import {
    getDefaultContactsCrmValue,
    getPredefinedMetadataOnCrmFieldChange,
} from "app/pages/sales-intelligence/common-components/export-table/utils";
import type { CrmFieldType } from "app/pages/sales-intelligence/common-components/export-table/types";
import {
    ExportSettingsSourceType,
    type ExportItem,
    type SalesforcePreDefinedType,
} from "app/pages/sales-intelligence/sub-modules/management/store/type";
import type { HubspotPreDefinedType } from "app/pages/sales-intelligence/sub-modules/management/store/types/shared";
import { getExportSettingSourceType } from "app/pages/sales-intelligence/sub-modules/management/utils/utils";

import type { CrmProvider, EnrichmentJobMappingDTO } from "../types/types";
import type { ReactElement } from "react";

type FetchCrmFields = (params: {
    columnName: string;
    sourceType: ExportSettingsSourceType;
    isPreDefined: boolean;
    preDefinedType: SalesforcePreDefinedType | HubspotPreDefinedType;
}) => void;

type Params = {
    crmProvider: CrmProvider;
    tableData: readonly ExportItem[];
    columns: CrmFieldType[];
    onAddMapping: (mapping: EnrichmentJobMappingDTO) => void;
    onRemoveMapping: (index: number) => void;
    onUpdateMapping: (index: number, changes: Partial<EnrichmentJobMappingDTO>) => void;
    fetchCrmFields: FetchCrmFields;
};

const ADD_FIELD_KEY = "internal_add";

const useMappingTableHandlers = ({
    crmProvider,
    tableData,
    columns,
    onAddMapping,
    onRemoveMapping,
    onUpdateMapping,
    fetchCrmFields,
}: Params) => {
    const findMappingIndex = (setting: ExportItem): number => {
        return tableData.indexOf(setting);
    };

    const onSimilarwebCellClick = (setting: ExportItem, newSetting: { id: string; text?: string }): void => {
        if (!newSetting || !setting) {
            return;
        }

        const { field } = setting;
        const { id } = newSetting;

        const isTechField = id.includes(TECHNOGRAPHICS_FIELD);
        const isPreDefined = id.includes(PRE_DEFINED_FIELD);
        const isSpecialField = isTechField || isPreDefined;

        if (field === ADD_FIELD_KEY) {
            const defaultCrmField = getDefaultContactsCrmValue(id, crmProvider);

            onAddMapping({
                externalField: isSpecialField ? "" : defaultCrmField || "",
                overwrite: false,
                sourceType: getExportSettingSourceType(id),
                metadata: isSpecialField ? "" : id,
            });
            return;
        }

        const settingIndex = findMappingIndex(setting);
        if (settingIndex < 0) {
            return;
        }

        const defaultCrmField = getDefaultContactsCrmValue(id, crmProvider);

        onUpdateMapping(settingIndex, {
            metadata: isSpecialField ? "" : id,
            sourceType: getExportSettingSourceType(id),
            externalField: isSpecialField ? "" : defaultCrmField || "",
        });
    };

    const onCrmCellClick = (setting: ExportItem, newSetting: { id: string; text?: string }): void => {
        const settingIndex = findMappingIndex(setting);
        if (settingIndex < 0) {
            return;
        }

        const newCrmField = newSetting.id;
        const updatedMetadata =
            setting.sourceType === ExportSettingsSourceType.Predefined
                ? getPredefinedMetadataOnCrmFieldChange(newCrmField, setting.externalField, columns)
                : null;

        onUpdateMapping(settingIndex, {
            externalField: newCrmField,
            ...(updatedMetadata !== null ? { metadata: updatedMetadata } : {}),
        });
    };

    const onToggleClick = ({
        setting,
        key,
        value,
    }: {
        setting: ExportItem;
        key: keyof ExportItem;
        value: boolean;
    }): void => {
        const settingIndex = findMappingIndex(setting);
        if (settingIndex >= 0) {
            onUpdateMapping(settingIndex, { [key]: value });
        }
    };

    const onExampleChange = (setting: ExportItem, value: string): void => {
        const settingIndex = findMappingIndex(setting);
        if (settingIndex >= 0) {
            onUpdateMapping(settingIndex, { metadata: value });
        }
    };

    const handleDeleteClick = (setting: ExportItem): void => {
        const settingIndex = findMappingIndex(setting);
        if (settingIndex >= 0) {
            onRemoveMapping(settingIndex);
        }
    };

    const getCrmFields = (
        columnName: string,
        sourceType?: ExportSettingsSourceType,
        isPreDefined?: boolean,
        preDefinedType?: SalesforcePreDefinedType | HubspotPreDefinedType,
    ): void => {
        fetchCrmFields({
            columnName,
            isPreDefined: isPreDefined ?? false,
            preDefinedType: preDefinedType as SalesforcePreDefinedType | HubspotPreDefinedType,
            sourceType: sourceType as ExportSettingsSourceType,
        });
    };

    const searchFilter = (term: string, item: ReactElement<{ text?: string }>): boolean => {
        return (item.props?.text ?? "").toLowerCase().includes(term.toLowerCase());
    };

    return {
        onSimilarwebCellClick,
        onCrmCellClick,
        onToggleClick,
        onExampleChange,
        handleDeleteClick,
        getCrmFields,
        searchFilter,
    };
};

export default useMappingTableHandlers;
